import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import './SpintossStats.css';

const MOIS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const StatistiquesAchats = () => {
  // ── Filtres ──────────────────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState(moment().startOf('year').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [chargeTypeFilter, setChargeTypeFilter] = useState('');
  const [localisationFilter, setLocalisationFilter] = useState('');
  const [fournisseurFilter, setFournisseurFilter] = useState('Tous');

  // ── Data ──────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState([]);
  const [evolutionMensuelle, setEvolutionMensuelle] = useState([]);
  const [fournisseursUniques, setFournisseursUniques] = useState([]);
  const [chargeTypes, setChargeTypes] = useState([]);
  const [localisationsUniques, setLocalisationsUniques] = useState([]);
  const [chargeTypesUniques, setChargeTypesUniques] = useState([]);

  // ── Charger les types de charges pour le filtre (plus nécessaire, chargé depuis GetPurchaseStats) ─

  // ── Fetch statistiques ────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('startDate', moment(startDate).startOf('day').toISOString());
      params.append('endDate', moment(endDate).endOf('day').toISOString());
      if (chargeTypeFilter) params.append('chargeType', chargeTypeFilter);
      if (localisationFilter) params.append('localisation', localisationFilter);

      const res = await axios.get(`/api/ChargeExp/GetPurchaseStats?${params.toString()}`);
      setStats(res.data.statsParFournisseur || []);
      setEvolutionMensuelle(res.data.evolutionMensuelle || []);
      setFournisseursUniques(['Tous', ...(res.data.fournisseursUniques || [])]);
      // Mettre à jour les listes de filtres dynamiques
      if (res.data.localisationsUniques) setLocalisationsUniques(res.data.localisationsUniques);
      if (res.data.chargeTypesUniques) setChargeTypesUniques(res.data.chargeTypesUniques);
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement des statistiques.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, chargeTypeFilter, localisationFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── KPIs ──────────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    if (!stats.length) return {};
    const totalGlobal = stats.reduce((s, f) => s + (f.totalAchats || 0), 0);
    const totalCommandes = stats.reduce((s, f) => s + (f.nombreCommandes || 0), 0);
    const topFournisseur = stats[0];
    // Calcul de la durée en mois
    const dureeJours = moment(endDate).diff(moment(startDate), 'days') + 1;
    const dureeMois = Math.max(dureeJours / 30, 1);
    const cadenceMoyenne = totalCommandes / dureeMois;
    return { totalGlobal, totalCommandes, topFournisseur, cadenceMoyenne, nbFournisseurs: stats.length };
  }, [stats, startDate, endDate]);

  // ── Données graphe barres (top fournisseurs) ───────────────────────────────
  const barData = useMemo(() => {
    const filtered = fournisseurFilter !== 'Tous'
      ? stats.filter(f => f._id === fournisseurFilter)
      : stats.slice(0, 10);
    return {
      labels: filtered.map(f => f._id || 'Inconnu'),
      values: filtered.map(f => parseFloat((f.totalAchats || 0).toFixed(3))),
    };
  }, [stats, fournisseurFilter]);

  // ── Données graphe évolution mensuelle ────────────────────────────────────
  const evolutionData = useMemo(() => {
    const targetFrs = fournisseurFilter !== 'Tous' ? fournisseurFilter : null;
    const evFiltered = targetFrs
      ? evolutionMensuelle.filter(e => e._id.fournisseur === targetFrs)
      : evolutionMensuelle;

    // Construire une grille de mois
    const start = moment(startDate);
    const end = moment(endDate);
    const moisKeys = [];
    let cur = start.clone().startOf('month');
    while (cur.isSameOrBefore(end, 'month')) {
      moisKeys.push(`${cur.year()}-${cur.month() + 1}`);
      cur.add(1, 'month');
    }

    // Agréger par mois
    const byMois = {};
    evFiltered.forEach(e => {
      const key = `${e._id.annee}-${e._id.mois}`;
      byMois[key] = (byMois[key] || 0) + (e.montantMois || 0);
    });

    return {
      labels: moisKeys.map(k => {
        const [y, m] = k.split('-');
        return `${MOIS_FR[parseInt(m) - 1]} ${y}`;
      }),
      values: moisKeys.map(k => parseFloat((byMois[k] || 0).toFixed(3))),
    };
  }, [evolutionMensuelle, fournisseurFilter, startDate, endDate]);

  // ── Données camembert ─────────────────────────────────────────────────────
  const pieData = useMemo(() => {
    return stats.slice(0, 8).map((f, i) => ({
      id: i,
      value: parseFloat((f.totalAchats || 0).toFixed(3)),
      label: f._id || 'Inconnu',
    }));
  }, [stats]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="spintoss-dashboard-container">
      {/* ── En-tête ── */}
      <div className="spintoss-header">
        <h1>📦 Statistiques des Achats</h1>
        <p>Analyse de la cadence d'achat par fournisseur • Suivi des dépenses sur la période</p>
      </div>

      {/* ── Filtres ── */}
      <div className="spintoss-filters">
        <div className="spintoss-filter-group">
          <label>Date début</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>
        <div className="spintoss-filter-group">
          <label>Date fin</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <div className="spintoss-filter-group">
          <label>Type d'achat</label>
          <select value={chargeTypeFilter} onChange={e => setChargeTypeFilter(e.target.value)}>
            <option value="">Tous les types</option>
            {chargeTypesUniques.map(ct => (
              <option key={ct} value={ct}>{ct}</option>
            ))}
          </select>
        </div>
        <div className="spintoss-filter-group">
          <label>Emplacement</label>
          <select value={localisationFilter} onChange={e => setLocalisationFilter(e.target.value)}>
            <option value="">Tous les emplacements</option>
            {localisationsUniques.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
        <div className="spintoss-filter-group">
          <label>Fournisseur</label>
          <select value={fournisseurFilter} onChange={e => setFournisseurFilter(e.target.value)}>
            {fournisseursUniques.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        <div className="spintoss-filter-group" style={{ justifyContent: 'flex-end' }}>
          <button
            onClick={fetchStats}
            style={{
              background: 'linear-gradient(135deg, #059669, #10b981)',
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '10px 24px', fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
            }}
          >
            🔄 Actualiser
          </button>
        </div>
      </div>

      {/* ── États de chargement / erreur ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#059669', fontSize: '1.1rem', fontWeight: '600' }}>
          ⏳ Chargement des statistiques...
        </div>
      )}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '16px', color: '#dc2626', marginBottom: '20px' }}>
          ⚠️ {error}
        </div>
      )}

      {!loading && !error && stats.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>Aucun achat enregistré pour cette période.</div>
          <div style={{ fontSize: '0.9rem', marginTop: '8px' }}>
            Modifiez les filtres ou commencez à saisir des achats dans <strong>Charges d'Exploitation</strong>.
          </div>
        </div>
      )}

      {!loading && stats.length > 0 && (
        <>
          {/* ── KPIs ── */}
          <div className="spintoss-kpis">
            <div className="spintoss-kpi-card">
              <div className="spintoss-kpi-title">💰 Total Achats</div>
              <div className="spintoss-kpi-value primary">{(kpis.totalGlobal || 0).toFixed(3)}</div>
              <small>TND sur la période</small>
            </div>
            <div className="spintoss-kpi-card">
              <div className="spintoss-kpi-title">🏬 Fournisseurs actifs</div>
              <div className="spintoss-kpi-value info">{kpis.nbFournisseurs || 0}</div>
              <small>Fournisseurs différents</small>
            </div>
            <div className="spintoss-kpi-card">
              <div className="spintoss-kpi-title">📋 Nb. Commandes</div>
              <div className="spintoss-kpi-value warning">{kpis.totalCommandes || 0}</div>
              <small>Commandes enregistrées</small>
            </div>
            <div className="spintoss-kpi-card">
              <div className="spintoss-kpi-title">📅 Cadence mensuelle</div>
              <div className="spintoss-kpi-value positive">{(kpis.cadenceMoyenne || 0).toFixed(1)}</div>
              <small>Commandes / mois (moy.)</small>
            </div>
            {kpis.topFournisseur && (
              <div className="spintoss-kpi-card" style={{ borderColor: 'rgba(245,158,11,0.3)' }}>
                <div className="spintoss-kpi-title">🥇 Top Fournisseur</div>
                <div className="spintoss-kpi-value warning" style={{ fontSize: '1.3rem' }}>
                  {kpis.topFournisseur._id}
                </div>
                <small>{(kpis.topFournisseur.totalAchats || 0).toFixed(3)} TND</small>
              </div>
            )}
          </div>

          {/* ── Graphiques ── */}
          <div className="spintoss-charts">
            {/* Bar chart — Top fournisseurs */}
            {barData.labels.length > 0 && (
              <div className="spintoss-chart-container">
                <div className="spintoss-chart-title">
                  📊 Achats par fournisseur (TND)
                </div>
                <BarChart
                  xAxis={[{ scaleType: 'band', data: barData.labels, tickLabelStyle: { fontSize: 11 } }]}
                  series={[{ data: barData.values, color: '#10b981', label: 'Total TND' }]}
                  width={400}
                  height={260}
                  margin={{ left: 70, right: 10, top: 10, bottom: 50 }}
                />
              </div>
            )}

            {/* Line chart — Évolution mensuelle */}
            {evolutionData.labels.length > 0 && (
              <div className="spintoss-chart-container">
                <div className="spintoss-chart-title">
                  📈 Évolution mensuelle des achats (TND)
                  {fournisseurFilter !== 'Tous' && <small style={{ color: '#6b7280', marginLeft: '8px' }}>— {fournisseurFilter}</small>}
                </div>
                <LineChart
                  xAxis={[{ scaleType: 'point', data: evolutionData.labels, tickLabelStyle: { fontSize: 11 } }]}
                  series={[{ data: evolutionData.values, color: '#3b82f6', label: 'Achats (TND)', area: true }]}
                  width={400}
                  height={260}
                  margin={{ left: 70, right: 10, top: 10, bottom: 50 }}
                />
              </div>
            )}

            {/* Pie chart — Répartition */}
            {pieData.length > 0 && (
              <div className="spintoss-chart-container">
                <div className="spintoss-chart-title">🥧 Répartition par fournisseur</div>
                <PieChart
                  series={[{
                    data: pieData,
                    innerRadius: 50,
                    outerRadius: 110,
                    paddingAngle: 3,
                    cornerRadius: 4,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                  }]}
                  width={400}
                  height={280}
                />
              </div>
            )}
          </div>

          {/* ── Tableau récapitulatif par fournisseur ── */}
          <div className="spintoss-table-container">
            <h3>📋 Détail par fournisseur</h3>
            <div className="table-responsive">
              <table className="spintoss-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fournisseur</th>
                    <th>Total Achats (TND)</th>
                    <th>Nb Commandes</th>
                    <th>Montant Moyen (TND)</th>
                    <th>Première Commande</th>
                    <th>Dernière Commande</th>
                    <th>Cadence</th>
                  </tr>
                </thead>
                <tbody>
                  {stats
                    .filter(f => fournisseurFilter === 'Tous' || f._id === fournisseurFilter)
                    .map((f, idx) => {
                      const dureeJours = moment(f.derniereCommande).diff(moment(f.premiereCommande), 'days') + 1;
                      const dureeMois = Math.max(dureeJours / 30, 1);
                      const cadence = (f.nombreCommandes / dureeMois).toFixed(1);
                      return (
                        <tr key={f._id}>
                          <td>
                            <span className="spintoss-badge-primary">{idx + 1}</span>
                          </td>
                          <td style={{ fontWeight: '700', color: '#1e293b' }}>{f._id || '—'}</td>
                          <td style={{ fontWeight: '700', color: '#059669' }}>
                            {(f.totalAchats || 0).toFixed(3)} TND
                          </td>
                          <td>{f.nombreCommandes || 0}</td>
                          <td>{(f.montantMoyen || 0).toFixed(3)} TND</td>
                          <td>{f.premiereCommande ? moment(f.premiereCommande).format('DD/MM/YYYY') : '—'}</td>
                          <td>{f.derniereCommande ? moment(f.derniereCommande).format('DD/MM/YYYY') : '—'}</td>
                          <td>
                            <span style={{
                              background: 'rgba(59,130,246,0.1)', color: '#2563eb',
                              padding: '3px 10px', borderRadius: '10px', fontWeight: '700', fontSize: '0.85rem'
                            }}>
                              {cadence} cmd/mois
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(59,130,246,0.1))', fontWeight: '800' }}>
                    <td colSpan={2}>TOTAL</td>
                    <td style={{ color: '#059669' }}>{(kpis.totalGlobal || 0).toFixed(3)} TND</td>
                    <td>{kpis.totalCommandes || 0}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StatistiquesAchats;
